import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import FormControlLabel from "@mui/material/FormControlLabel";
import InputAdornment from "@mui/material/InputAdornment";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Switch from "@mui/material/Switch";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import AddIcon from "@mui/icons-material/Add";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import SearchIcon from "@mui/icons-material/Search";
import SearchOffIcon from "@mui/icons-material/SearchOff";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";

import Langs, { Localize } from "../../app/lang/langs";
import CourseCard from "../../components/vlearn/CourseCard";
import {
    VLearnCardSkeletons, VLearnEmptyState,
    vlearnGrid, vlearnSurface, vlearnType
} from "../../components/vlearn/vlearnUi";
import { listVLearnCategories, listVLearnCourses } from "./vlearnSlice";

/**
 * V-Learning — the course catalogue.
 *
 * Two scopes rather than one list with a filter, because they are two jobs. "My learning" is
 * what this person has to do; "Courses I manage" is what they are responsible for other
 * people doing. Mixing them means a manager's own three mandatory courses are lost among the
 * forty they administer.
 *
 * The scope is sent to the server, not applied here. `listCourses` intersects it with the
 * caller's reach in SQL and never widens it, so the rows that arrive are already the ones
 * this person may see — which is also why the manage tab appears from `state.vlearn.reach`
 * and not from a client-side reading of the permissions array.
 *
 * ── Filtering goes to the server ─────────────────────────────────────────────────────
 *
 * Unlike V-Resume, whose list is capped and filtered in memory, the catalogue is paginated
 * and the search runs against `title`/`subtitle`/`description` in SQL. So the text box is
 * debounced instead: a request per keystroke would be a dozen queries to type "sécurité", and
 * the responses could arrive out of order.
 */

// Long enough that an ordinary typing speed produces one request per word, short enough that
// the list does not feel detached from the box.
const SEARCH_DEBOUNCE_MS = 350

const focusRing = {
    '&:focus-visible': {
        outline: '2px solid var(--orange, rgb(255,144,21))',
        outlineOffset: '2px'
    }
}

// Matches `listCourses`' own default, so the first request behaves exactly as before and
// only the follow-ups are new. The server clamps any limit to 200 per request.
const CATALOG_PAGE_SIZE = 60

export default function CourseCatalog({
    onOpenCourse, onEditCourse, onCreateCourse, initialScope, onOpenSettings, onScopeChange
}) {

    const dispatch = useDispatch()

    const catalog = useSelector((state) => state.vlearn.catalog)
    const catalogLoading = useSelector((state) => state.vlearn.catalogLoading)
    const catalogTotal = useSelector((state) => state.vlearn.catalogTotal)
    const catalogSummary = useSelector((state) => state.vlearn.catalogSummary)
    const categories = useSelector((state) => state.vlearn.categories)
    const reach = useSelector((state) => state.vlearn.reach)
    const lang = useSelector((state) => state.settings.lang)

    const canManage = !!(reach && (reach.canCreate || reach.hasTeamReach))

    /**
     * Which tab opens first.
     *
     * 'learning' for a fresh visit, because that is the job almost everyone here has. But
     * VLearnView passes 'managing' when the user is coming back from authoring — after
     * creating a course, landing on "My learning" would show a list the new draft is not in,
     * which reads as "it wasn't created".
     */
    const [scope, setScope] = useState(initialScope === 'managing' ? 'managing' : 'learning')

    /**
     * How many courses to ask for.
     *
     * `listCourses` defaults to 60 and this screen never sent a `limit` or read the `total` it
     * returns, so a company with more than 60 published courses in scope simply did not see
     * the rest — no message, no control, nothing on screen to suggest anything was missing.
     * The docblock above claimed the catalogue was paginated; this is what makes that true.
     */
    const [limit, setLimit] = useState(CATALOG_PAGE_SIZE)
    const [search, setSearch] = useState('')
    const [debouncedSearch, setDebouncedSearch] = useState('')
    const [idCategory, setIdCategory] = useState('')
    const [mandatoryOnly, setMandatoryOnly] = useState(false)

    // ── Fetching ─────────────────────────────────────────────────────────────────────

    // The categories referential does not change during a session, so it is fetched once.
    // Guarded with a ref the way `VSecuriteView` does it: React 18's StrictMode mounts every
    // component twice in development, and an unguarded mount effect fires both times.
    const mounted = useRef(false)
    useEffect(() => {
        if (mounted.current) return
        mounted.current = true
        dispatch(listVLearnCategories({}))
    }, [dispatch])

    /**
     * The last query actually sent.
     *
     * This effect depends on the filters, so StrictMode's double mount would otherwise issue
     * the same request twice before the user has touched anything. Comparing against the last
     * body sent is a stronger guard than a mount flag here, because it also collapses the
     * no-op re-runs a parent re-render can cause.
     */
    const lastQueryRef = useRef(null)
    useEffect(() => {
        const body = {
            scope,
            search: debouncedSearch || undefined,
            id_category: idCategory || undefined,
            mandatory_only: mandatoryOnly ? 1 : undefined,
            limit
        }
        const signature = JSON.stringify(body)
        if (lastQueryRef.current === signature) return
        lastQueryRef.current = signature
        dispatch(listVLearnCourses(body))
    }, [dispatch, scope, debouncedSearch, idCategory, mandatoryOnly, limit])

    // Back to one page whenever the filters change: a limit grown to 240 by earlier clicks
    // would otherwise fetch 240 rows for a search the user has only just started typing.
    useEffect(() => {
        setLimit(CATALOG_PAGE_SIZE)
    }, [scope, debouncedSearch, idCategory, mandatoryOnly])

    // ── The debounce ─────────────────────────────────────────────────────────────────

    // A ref timer rather than a debounce utility: one `setTimeout` is the whole requirement,
    // and it is cleared on unmount so a pending keystroke cannot dispatch into a dead screen.
    const debounceRef = useRef(null)
    const onSearchChange = useCallback((event) => {
        const value = event.target.value
        setSearch(value)
        if (debounceRef.current) clearTimeout(debounceRef.current)
        debounceRef.current = setTimeout(() => setDebouncedSearch(value.trim()), SEARCH_DEBOUNCE_MS)
    }, [])

    useEffect(() => () => {
        if (debounceRef.current) clearTimeout(debounceRef.current)
    }, [])

    // ── Derived ──────────────────────────────────────────────────────────────────────

    const hasFilters = !!(debouncedSearch || idCategory || mandatoryOnly)
    const rows = catalog || []
    // Falls back to what is loaded if the server sent no total, so the header never reads 0
    // while cards are on screen.
    const resultCount = Number.isFinite(Number(catalogTotal)) && Number(catalogTotal) > 0
        ? Number(catalogTotal) : rows.length

    /**
     * The learner's own headline numbers, counted by the server.
     *
     * These used to be counted here from the rows on screen, on the reasoning that the strip
     * should describe the same list as the grid. That held while the screen showed every
     * matching row — once the catalogue paginated at 60 it stopped holding, and the strip
     * quietly described 60 courses while the header beside it said 66. `listCourses` now folds
     * the counts into the query that produces `total`, so they cover the whole result set,
     * respect the same filters, and cost no extra request.
     */
    const summary = useMemo(() => ({
        inProgress: Number(catalogSummary && catalogSummary.in_progress) || 0,
        overdue: Number(catalogSummary && catalogSummary.overdue) || 0,
        completed: Number(catalogSummary && catalogSummary.completed) || 0
    }), [catalogSummary])

    const clearFilters = useCallback(() => {
        if (debounceRef.current) clearTimeout(debounceRef.current)
        setSearch('')
        setDebouncedSearch('')
        setIdCategory('')
        setMandatoryOnly(false)
    }, [])

    /**
     * "Create a course" belongs to the managing tab only.
     *
     * The two tabs are two jobs: "My learning" is what this person has to do, "Courses I
     * manage" is what they are responsible for other people doing. Authoring is the second
     * job, so the button has no place on the first — a manager looking at their own three
     * mandatory courses is wearing the learner's hat, and an author button there reads as
     * "create a course for yourself".
     *
     * Gating it here rather than at the two call sites covers both: the header renders this
     * and so does the empty state, which is exactly where it was most wrong — an employee
     * with no courses assigned was being invited to create one.
     */
    const createButton = (reach && reach.canCreate && scope === 'managing') ? (
        <Button
            size="small"
            variant="contained"
            startIcon={<AddIcon sx={{ fontSize: 17 }} />}
            onClick={() => onCreateCourse && onCreateCourse()}
            sx={{
                ...focusRing, textTransform: 'none', fontSize: '0.82rem', flexShrink: 0,
                boxShadow: 'none',
                backgroundColor: 'var(--orange, rgb(255,144,21))',
                '&:hover': { backgroundColor: '#e07f0d', boxShadow: 'none' }
            }}
        >
            <Langs str="VLEARN_CREATE_COURSE" />
        </Button>
    ) : null

    return (
        /*
         * The 60px top offset is desktop breathing room, not clearance for a bar: below 768px
         * MenuBar renders MobileMenu (a fixed opener at the BOTTOM right) and sets
         * `--sidebar-w: 0`, so nothing occupies the top of a phone screen. Sixty blank pixels
         * there is a tenth of a 640px viewport spent on nothing.
         *
         * The two paddings are also halved: 20px here plus 20px on `.bodyCard` took 80px of
         * horizontal room out of a 336px content box, which is what pushed the card grid past
         * the viewport.
         */
        <Box sx={{
            marginTop: { xs: '14px', sm: '60px' },
            padding: { xs: '8px', sm: '20px' },
            minHeight: 'calc(100vh - 100px)'
        }}>
            <Box className="bodyCard" sx={{ margin: 0, padding: { xs: '14px', sm: '20px' } }}>

                {/* ── Header ──────────────────────────────────────────────────── */}
                <Box sx={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    gap: 2, flexWrap: 'wrap'
                }}>
                    <Box>
                        <Typography component="h1" sx={{ ...vlearnType.pageTitle }}>
                            <Langs str="VLEARN_CATALOG_TITLE" />
                        </Typography>
                        <Typography sx={{ ...vlearnType.body, mt: 0.25 }}>
                            <Langs str="VLEARN_CATALOG_SUBTITLE" />
                        </Typography>
                    </Box>
                    {/* The two header actions sit in one group so they wrap together on a
                        narrow screen rather than one dropping to its own line. */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        {/* Admins only, and this is the module's only route to the settings —
                            VLearnView no longer renders a header. Presentation only: the
                            server refuses a non-admin write regardless. */}
                        {onOpenSettings ? (
                            <Button
                                size="small"
                                onClick={onOpenSettings}
                                startIcon={<SettingsOutlinedIcon sx={{ fontSize: 17 }} />}
                                sx={{
                                    ...focusRing, textTransform: 'none', fontSize: '0.82rem',
                                    flexShrink: 0, color: 'var(--text-secondary, #4b5263)'
                                }}
                            >
                                <Langs str="VLEARN_SETTINGS_TITLE" />
                            </Button>
                        ) : null}
                        {createButton}
                    </Box>
                </Box>

                {/* ── Scope ───────────────────────────────────────────────────── */}
                {canManage ? (
                    <Tabs
                        value={scope}
                        onChange={(event, value) => {
                            setScope(value)
                            // Reported up so the tab survives a trip into a course and back.
                            // This component unmounts on every screen change, so it cannot
                            // remember on its own.
                            if (onScopeChange) onScopeChange(value)
                        }}
                        sx={{
                            mt: 2,
                            borderBottom: '1px solid rgba(0,0,0,0.08)',
                            minHeight: 40,
                            '& .MuiTab-root': {
                                textTransform: 'none', fontSize: '0.86rem', fontWeight: 600,
                                minHeight: 40, padding: '8px 14px',
                                '&:focus-visible': {
                                    outline: '2px solid var(--orange, rgb(255,144,21))',
                                    outlineOffset: '-2px'
                                }
                            },
                            '& .Mui-selected': { color: 'var(--orange, rgb(255,144,21)) !important' },
                            '& .MuiTabs-indicator': { backgroundColor: 'var(--orange, rgb(255,144,21))' }
                        }}
                    >
                        <Tab value="learning" label={Localize('VLEARN_TAB_MY_LEARNING', lang)} />
                        <Tab value="managing" label={Localize('VLEARN_TAB_MANAGING', lang)} />
                        {/* Admins only. `resolveCourseAccess` already grants an admin edit and
                            dashboard on every course, but neither existing tab lists them —
                            "My learning" needs an enrolment and "Courses I manage" needs a
                            manager row — so an admin could open any course by id and find
                            none of them. A third tab rather than widening "Courses I manage",
                            which would then not mean what it says. */}
                        {reach && reach.isAdmin ? (
                            <Tab value="all" label={Localize('VLEARN_TAB_ALL_COURSES', lang)} />
                        ) : null}
                    </Tabs>
                ) : null}

                {/* ── The learner's headline numbers ──────────────────────────── */}
                {scope === 'learning' ? (
                    <Box sx={{
                        ...vlearnSurface.card, mt: 2.5, padding: '14px 18px',
                        display: 'flex', flexWrap: 'wrap', gap: { xs: 2, sm: 4 }
                    }}>
                        <SummaryStat labelKey="VLEARN_SUMMARY_IN_PROGRESS"
                            value={summary.inProgress} color="var(--orange, rgb(255,144,21))" />
                        <SummaryStat labelKey="VLEARN_SUMMARY_OVERDUE"
                            value={summary.overdue} color="var(--red, rgb(251,105,98))" />
                        <SummaryStat labelKey="VLEARN_SUMMARY_COMPLETED"
                            value={summary.completed} color="var(--green, rgb(58,156,96))" />
                    </Box>
                ) : null}

                {/* ── Filters ─────────────────────────────────────────────────── */}
                {/* Stacked below `sm`. Three controls sharing a 256px row wrapped into a
                    ragged three-line block whose search box was narrower than its own
                    placeholder; one full-width control per line reads and taps better. */}
                <Box sx={{
                    mt: 2.5, display: 'flex', gap: 1.5,
                    flexDirection: { xs: 'column', sm: 'row' },
                    // `wrap` only means anything on the row axis. Left on in a column it lets
                    // items spill into a second COLUMN instead of stacking.
                    flexWrap: { xs: 'nowrap', sm: 'wrap' },
                    alignItems: { xs: 'stretch', sm: 'center' }
                }}>
                    <TextField
                        size="small"
                        value={search}
                        onChange={onSearchChange}
                        placeholder={Localize('VLEARN_SEARCH_PLACEHOLDER', lang)}
                        inputProps={{ 'aria-label': Localize('VLEARN_SEARCH_PLACEHOLDER', lang) }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon sx={{ fontSize: 18, color: 'var(--text-muted, #8b92a0)' }} />
                                </InputAdornment>
                            )
                        }}
                        sx={{
                            /*
                             * THE BASIS IS A HEIGHT BELOW `sm`.
                             *
                             * This row is `flexDirection: column` on a phone, and `flex-basis`
                             * follows the main axis — so `1 1 240px` told the field to be 240px
                             * TALL, leaving a dead band under the input. Sized by content when
                             * stacked; the width intent only applies once it is a row again.
                             */
                            flex: { xs: '0 0 auto', sm: '1 1 240px' },
                            width: { xs: '100%', sm: 'auto' },
                            // `minWidth: 200` was a floor a 256px content box could not
                            // always honour next to a wrapped sibling; the column layout
                            // below `sm` makes a floor unnecessary there.
                            minWidth: { xs: 0, sm: 200 },
                            maxWidth: { xs: '100%', sm: 420 },
                            '& .MuiOutlinedInput-root': {
                                borderRadius: '10px',
                                fontSize: { xs: '16px', sm: '0.85rem' }
                            }
                        }}
                    />

                    <Select
                        size="small"
                        displayEmpty
                        value={idCategory}
                        onChange={(event) => setIdCategory(event.target.value)}
                        inputProps={{ 'aria-label': Localize('VLEARN_CATEGORY', lang) }}
                        sx={{
                            // Same trap as the search field above: `0 1 200px` in a column
                            // container is 200px of HEIGHT, which is what turned this select
                            // into a tall empty box with its value stranded at the bottom.
                            flex: { xs: '0 0 auto', sm: '0 1 200px' },
                            width: { xs: '100%', sm: 'auto' },
                            minWidth: { xs: 0, sm: 160 },
                            borderRadius: '10px',
                            fontSize: { xs: '16px', sm: '0.85rem' }
                        }}
                    >
                        <MenuItem value=""><Langs str="VLEARN_ALL_CATEGORIES" /></MenuItem>
                        {(categories || []).map(category => (
                            <MenuItem key={category.id} value={category.id}>{category.name}</MenuItem>
                        ))}
                    </Select>

                    <FormControlLabel
                        control={
                            <Switch
                                size="small"
                                checked={mandatoryOnly}
                                onChange={(event) => setMandatoryOnly(event.target.checked)}
                            />
                        }
                        label={
                            <Typography sx={{ ...vlearnType.body }}>
                                <Langs str="VLEARN_MANDATORY_ONLY" />
                            </Typography>
                        }
                        // A `size="small"` switch is a 20px thumb in a 38px row — under the
                        // tap-target floor. The label is part of the target, so raising the
                        // row height raises the whole control.
                        sx={{ ml: 0, mr: 0, minHeight: 44 }}
                    />
                </Box>

                {/* ── Results ─────────────────────────────────────────────────── */}
                {!catalogLoading && rows.length ? (
                    <Typography sx={{ ...vlearnType.meta, mt: 2 }} aria-live="polite">
                        {/* The size of the RESULT SET, not of the page loaded so far.
                            Using rows.length made the header read "60 courses" while the
                            footer directly below it said "Showing 60 of 66". `total` is what
                            the server counted before applying the limit. */}
                        <Langs
                            str={resultCount === 1 ? 'VLEARN_N_COURSE_ONE' : 'VLEARN_N_COURSE_MANY'}
                            params={{ count: resultCount }}
                        />
                    </Typography>
                ) : null}

                <Box sx={{ ...vlearnGrid.cards, mt: 2 }}>
                    {catalogLoading ? <VLearnCardSkeletons count={6} /> : null}
                    {!catalogLoading ? rows.map(course => (
                        <CourseCard
                            key={course.id}
                            course={course}
                            // The tab travels with the click: entering a course from "My
                            // learning" is the learner journey, and the player should not
                            // then offer Dashboard and Edit just because this person also
                            // happens to author it.
                            onOpen={(id) => onOpenCourse && onOpenCourse(id, scope)}
                            onEdit={onEditCourse}
                            // Tells the card which job this tab is for, so a course the user
                            // both manages and is enrolled in does not offer "Start" and
                            // "Edit" side by side.
                            scope={scope}
                        />
                    )) : null}
                </Box>

                {/* Only when the server says there is more than was returned. `total` is the
                    count before the LIMIT, which listCourses has always sent. */}
                {!catalogLoading && rows.length && catalogTotal > rows.length ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, mt: 3 }}>
                        <Typography sx={{ ...vlearnType.meta }}>
                            <Langs str="VLEARN_SHOWING_COUNT"
                                params={{ shown: String(rows.length), total: String(catalogTotal) }} />
                        </Typography>
                        <Button
                            size="small"
                            variant="outlined"
                            onClick={() => setLimit((current) => current + CATALOG_PAGE_SIZE)}
                            sx={{ ...focusRing, textTransform: 'none', fontSize: '0.82rem' }}
                        >
                            <Langs str="VLEARN_LOAD_MORE" />
                        </Button>
                    </Box>
                ) : null}

                {!catalogLoading && !rows.length ? (
                    <VLearnEmptyState
                        icon={hasFilters ? <SearchOffIcon /> : <SchoolOutlinedIcon />}
                        title={<Langs str={emptyTitleKey()} />}
                        description={<Langs str={emptyDescriptionKey()} />}
                        action={hasFilters
                            ? (
                                <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={clearFilters}
                                    sx={{
                                        ...focusRing, textTransform: 'none', fontSize: '0.82rem',
                                        borderColor: 'rgba(0,0,0,0.18)', color: 'var(--text-primary, #111318)'
                                    }}
                                >
                                    <Langs str="VLEARN_CLEAR_FILTERS" />
                                </Button>
                            )
                            : createButton}
                    />
                ) : null}
            </Box>
        </Box>
    )

    function emptyTitleKey() {
        if (hasFilters) return 'VLEARN_EMPTY_FILTERED_TITLE'
        // 'all' is the admin tab: an empty one means the instance has no courses, not
        // that none are assigned to this person.
        if (scope === 'all') return 'VLEARN_EMPTY_ALL_TITLE'
        return scope === 'managing' ? 'VLEARN_EMPTY_MANAGING_TITLE' : 'VLEARN_EMPTY_LEARNING_TITLE'
    }

    function emptyDescriptionKey() {
        if (hasFilters) return 'VLEARN_EMPTY_FILTERED_DESC'
        if (scope === 'all') return 'VLEARN_EMPTY_ALL_DESC'
        return scope === 'managing' ? 'VLEARN_EMPTY_MANAGING_DESC' : 'VLEARN_EMPTY_LEARNING_DESC'
    }
}

/** One figure in the summary strip. */
function SummaryStat({ labelKey, value, color }) {
    return (
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
            <Typography sx={{ fontSize: '1.5rem', fontWeight: 700, lineHeight: 1, color }}>
                {value}
            </Typography>
            <Typography sx={{
                ...vlearnType.meta, fontWeight: 600,
                textTransform: 'uppercase', letterSpacing: '.04em'
            }}>
                <Langs str={labelKey} />
            </Typography>
        </Box>
    )
}
